import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * A strip of portrait videos that play only while they are on screen.
 *
 * Layout is CSS scroll-snap in sections/lift-ugc.liquid; this class only starts
 * and stops the videos and keeps the pause controls honest. Same split as
 * lift-gallery.
 *
 * Five clips playing at once is what the IntersectionObserver avoids — the
 * prototype does the same (html-blocks/index.html:484). Hence no `autoplay`
 * attribute in the markup: with one, all five would run before the observer
 * ever reported.
 *
 * @extends {Component}
 */
class LiftUgc extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  /**
   * The theme editor re-renders a section through the Section Rendering API,
   * which replaces the videos. #setup is written to be safe to run again.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #setup() {
    this.#observer?.disconnect();
    this.#observer = null;

    this.#wireVideos();
    this.#syncButtons();

    // Under reduced motion nothing starts by itself and no observer exists, so
    // the control is the only way in — it reads as a play button, because
    // #syncButtons has just found every video paused.
    if (prefersReducedMotion()) return;

    this.#observer = new IntersectionObserver(this.#onIntersect, { threshold: 0.25 });
    for (const item of this.#items) this.#observer.observe(item);
  }

  /** @returns {HTMLElement[]} */
  get #items() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-lift-ugc-item]')]);
  }

  /**
   * @param {Element | null} item
   * @returns {HTMLVideoElement | null}
   */
  #videoIn(item) {
    return item?.querySelector('video') ?? null;
  }

  /** Idempotent: a re-render replaces the videos, and listeners go with them. */
  #wireVideos() {
    for (const video of this.querySelectorAll('video')) {
      video.removeEventListener('play', this.#syncButtons);
      video.removeEventListener('pause', this.#syncButtons);
      video.addEventListener('play', this.#syncButtons);
      video.addEventListener('pause', this.#syncButtons);
    }
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onIntersect = (entries) => {
    for (const entry of entries) {
      const item = /** @type {HTMLElement} */ (entry.target);
      const video = this.#videoIn(item);
      if (!video) continue;

      if (!entry.isIntersecting) {
        video.pause();
        continue;
      }

      // A video the visitor stopped by hand stays stopped, however often it
      // scrolls back into view. Without this flag the observer would undo the
      // press on the next scroll, and WCAG 2.2.2 would not be met.
      if (item.dataset.liftPaused === 'true') continue;

      video.play().catch(() => {});
    }
  };

  /**
   * The control on one item. Horizon's <deferred-media> cannot cover this case:
   * its play/pause hint is gated on [data-media-loaded] (assets/media.js:127),
   * which an inline autoplaying video never gets.
   *
   * @param {Event} event
   */
  toggleVideo(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const item = target.closest('[data-lift-ugc-item]');
    const video = this.#videoIn(item);
    if (!item || !video) return;

    if (video.paused) {
      delete (/** @type {HTMLElement} */ (item).dataset.liftPaused);
      video.play().catch(() => {});
    } else {
      /** @type {HTMLElement} */ (item).dataset.liftPaused = 'true';
      video.pause();
    }
    // The buttons are not updated here on purpose — the play and pause events
    // do it, so the control also tells the truth when a play was refused or the
    // video stopped on its own.
  }

  /**
   * Reads the state off the videos rather than tracking it, so a button can
   * never drift from what is actually on screen.
   */
  #syncButtons = () => {
    for (const item of this.#items) {
      const button = item.querySelector('[data-lift-ugc-pause]');
      const video = this.#videoIn(item);
      if (!button || !video) continue;

      const paused = video.paused;
      button.setAttribute('aria-pressed', String(paused));

      const label = button.querySelector('[data-lift-label]');
      const text = /** @type {HTMLElement} */ (button).dataset[
        paused ? 'liftLabelPlay' : 'liftLabelPause'
      ];
      if (label && text) label.textContent = text;
    }
  };
}

if (!customElements.get('lift-ugc-component')) {
  customElements.define('lift-ugc-component', LiftUgc);
}
